import { PermissionAction } from '../permission/permissions.model';
import { GasMonitorReading, GasSensorReading, InstrumentStatus, SensorGasCode, SensorStatus, SensorUnit } from '../gas/gas';
import { AnyItem, ItemType } from '../item/item';
import { LocationDetails } from '../location/location-update';
import { Point } from '../location/point';
import { ConnectivityStatus } from './badge-update';
import { MessageAttachment } from '../channel/channel-message';
import type { WranglerPlacement } from '../walt/walt';
import { CapAlertCertainty, CapAlertSeverity, CapAlertUrgency } from '../weather/weather-alert';

export type WaltEventTypes = EventType.WaltAssigned | EventType.WaltUnassigned | EventType.WaltDocked | EventType.WaltUndocked
    | EventType.WaltLogin | EventType.WaltLogout | EventType.WaltPoweredOff | EventType.WaltLocation | EventType.WaltFacilityChange;

export interface Event extends AbstractEvent {
    location?: [number, number];
    level?: number;
    accuracy?: number;
    offline?: boolean;
    stationary?: boolean;
    engine?: boolean;
    battery?: number;
    network?: NetworkStatus;
    connectivity?: ConnectivityStatus;
    panic?: boolean;
    message?: string;
    gasMonitor?: GasMonitorReading;
    peers?: Array<{
        id?: string;
        distance?: number;
        rssi?: number;
    }>;
    beacons?: Array<{
        id?: string;
        distance?: number;
        rssi?: number;
    }>;
    locationDate?: Date;
    lastMoving?: Date;
    lastInVehicle?: Date;
    locationType?: string;
    deviceType?: 'walt' | 'android' | 'ios';
    deviceId?: string;
    image?: {
        id?: string;
        image?: string;
        thumbnail?: string;
        imageIndex?: string;
        date?: Date;
        video?: string;
        duration?: number;
        sequence?: number;
        encoding?: string;
    };
    grantedLocationPermissions?: {
        fine: boolean;
        coarse: boolean;
        background: boolean;
        motion?: boolean;
    };
    locationDetails?: LocationDetails;
}

export type EventId = {
    itemId?: string;
    personId?: string;
    beaconId?: string;
    waltId?: string;
    date: Date;
    id: string;
};

export interface AbstractEvent {
    id?: Partial<EventId>;
    // when the event was stored from a message that was buffered offline and replayed later; the
    // original event time stays on id.date for sort, mirroring channel messages' uploaded field
    uploaded?: Date;
    facilityId?: string;
    facilityIds?: string[];
    registrationId?: string; // wisp id
    type?: EventType;
    folderId?: string;
    personName?: string;
    personTags?: string[];
    personCrafts?: string[];
    personCompany?: string;
    itemName?: string;
    itemTags?: string[];
}

export interface NetworkStatus {
    wanDevices: NetworkWanDevice[];
    gps: NetworkWanGps;
    updated?: Date;
    status?: NetworkStatusType;
}

export interface NetworkWanDevice {
    id: string;
    type: NetworkWanDeviceType;
    name?: string;
    sim?: string;
    carrier?: string;
    dbm?: string;
    rfBand?: string;
    serviceType?: string;
    ssid?: string; // only for wifi wan
}

export enum NetworkWanDeviceType {
    Cellular = 'cellular',
    Ethernet = 'ethernet',
    Wifi = 'wifi',
    Unknown = 'unknown',
}

export interface NetworkWanGps {
    location: number[];
    time: string;
}

export enum NetworkStatusType {
    Disconnected = 'disconnected',
    Private = 'private',
    Verizon = 'verizon',
    Other = 'other',
}

export enum EventType {
    AirReadingSubmission = 'air-reading-submission',
    BeaconLowBattery = 'beacon-low-battery',
    BeaconDisconnect = 'beacon-disconnect',
    BeaconEnter = 'beacon-enter',
    BeaconExit = 'beacon-exit',
    EngineOff = 'engine-off',
    EngineOn = 'engine-on',
    FacilityEnter = 'facility-enter',
    FacilityExit = 'facility-exit',
    FormAnswerSubmission = 'form-answer-submission',
    FormSubmission = 'form-submission',
    GasCodeReading = 'gas-code-reading',
    GasInstrumentReading = 'gas-instrument-reading',
    GasLevelReading = 'gas-level-reading',
    GasMonitorPair = 'gas-monitor-pair',
    GasMonitorUnpair = 'gas-monitor-unpair',
    GasMonitorReading = 'gas-monitor-reading',
    GeofenceDisconnect = 'geofence-disconnect',
    GeofenceEnter = 'geofence-enter',
    GeofenceExit = 'geofence-exit',
    LevelEnter = 'level-enter',
    LevelExit = 'level-exit',
    MessageDelivered = 'message-delivered',
    MobileLocationDenied = 'mobile-location-denied',
    MobileLogin = 'mobile-login',
    MobileLogout = 'mobile-logout',
    Moving = 'moving',
    None = 'none',
    PanicReceived = 'panic-received',
    PeerEnter = 'peer-enter',
    PeerExit = 'peer-exit',
    SafetyObservationResolved = 'safety-observation-resolved',
    SafetyObservationSubmitted = 'safety-observation-submitted',
    SafetyObservationUpdated = 'safety-observation-updated',
    Stationary = 'stationary',
    WaltAssigned = 'walt-assigned',
    WaltDocked = 'walt-docked',
    WaltLocation = 'walt-location',
    WaltLogin = 'walt-login',
    WaltLogout = 'walt-logout',
    WaltPoweredOff = 'walt-powered-off',
    WaltRepeaterPowerLoss = 'walt-repeater-power-loss',
    WaltUnassigned = 'walt-unassigned',
    WaltFacilityChange = 'walt-facility-change',
    WaltUndocked = 'walt-undocked',
    WeatherAlert = 'weather-alert',
    WeatherAlertResolved = 'weather-alert-resolved',
    /**
     * All-clear for a Warning-level alert specifically (its `event` name ends in "Warning", e.g.
     * `Tornado Warning`) - a distinct type from `WeatherAlertResolved` so a workflow can be scoped
     * to only the higher-urgency all-clear, per the PRD's recommendation that all-clear only
     * apply to Warning-level workflows, not Watch-level ones.
     */
    WeatherAlertWarningResolved = 'weather-alert-warning-resolved',
}


/**
 * How an SOS (PanicReceived) event was triggered. Absent on mesh-delivered SOSes —
 * the mesh panic packet carries no trigger type.
 */
export enum SosTriggerType {
    Sos = 'sos',
    Panic = 'panic',
    HardFall = 'hard-fall',
    Inactivity = 'inactivity',
}

export type BadgeLocation = [number, number];

export enum WorkflowActionType {
    Alert = 'alert',
    Post = 'post',
    Form = 'form',
    Report = 'report',
    Sms = 'sms',
    Email = 'email',
    Notify = 'notify',
    Tag = 'tag',
    FacilityAlert = 'facility-alert',
}

export interface AirReadingSubmissionEvent extends AbstractEvent {
    type: EventType.AirReadingSubmission;
    airReadingSubmissionId?: string;
    itemId?: string;
}

export interface EventUpdateResponse {
    date: Date;
    personId?: string;
    deviceId?: string;
    events?: AnyBadgeEvent[];
    itemId?: string;
    registrationId?: string;
    gasMonitor?: GasMonitorReading;
    panic?: boolean;
    location?: Point;
    locationDetails?: LocationDetails;
    facilities?: Array<{ id: string }>;
    lastMoving?: Date;
    lastInVehicle?: Date;
}

export interface FormSubmissionEvent extends AbstractEvent {
    type: EventType.FormSubmission;
    formSubmissionId?: string;
    formId?: { id: string, version: string };
    formName?: string;
    formTags?: string[];
    itemId?: string;
}

export interface GasMonitorEvent extends AbstractEvent {
    type: EventType.GasCodeReading | EventType.GasInstrumentReading;
    instrumentStatus?: InstrumentStatus;
    sensorCode?: SensorGasCode;
    sensorStatus?: SensorStatus;
    sensorUnit?: SensorUnit;
    sensorValue?: number;
}

export interface GeofenceEvent extends AbstractEvent {
    type: EventType.GeofenceEnter | EventType.GeofenceExit | EventType.GeofenceDisconnect;
    disconnect?: boolean;
    geofenceId?: string;
    geofenceName?: string;
    geofenceTags?: string[];
    geofenceEntered?: Date;
    geofenceTypeId?: string;
    geofenceCategories?: string[];
    visiting?: string;
}

export interface LogoutEvent extends AbstractEvent {
    type: EventType.MobileLogout;
    geoFenceId?: string;
    geoFenceName?: string;
    geoFenceTags?: string[];
}

export interface PanicEvent extends AbstractEvent {
    type: EventType.PanicReceived;
    message?: string;
    location?: [number, number];
    offline?: boolean;
    geofenceId?: string;
    geofenceName?: string;
    /** How the SOS was triggered; absent on mesh-delivered or pre-trigger-type client SOSes. */
    sosType?: SosTriggerType;
    /** Closest facility-registered beacon the device could hear when the SOS was raised; distinct from beaconId/beaconName, which mean "beacon entered". */
    proximityBeaconId?: string;
    proximityBeaconName?: string;
    proximityBeaconLevel?: number;
}

export interface UpdateEvent {
    date: Date;
    personId?: string;
    type?: EventType;
}

export type EventNameLookUpOverrides = { [type in EventType]?: (row: AnyBadgeEvent) => string };


export interface ItemUpdate {
    items?: { [key: string]: AnyItem };
    itemTypes?: { [key: string]: ItemType };
}

export const eventTypeToPermissions: { [key in EventType]?: PermissionAction } = {
    [EventType.FormSubmission]: PermissionAction.ViewForms,
};

export const mapExitToDisconnectEvent = (x: AnyBadgeEvent[]) => x.map(e => {
    if ((e as GeofenceEvent).disconnect) {
        switch (e.type) {
            case EventType.GeofenceExit:
                e.type = EventType.GeofenceDisconnect;
                break;
            case EventType.BeaconExit:
                e.type = EventType.BeaconDisconnect;
                break;
        }
    }
    return e;
});

export type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>;
};

export type RowEventMatch = EventType | RecursivePartial<AnyBadgeEvent>;

export enum RowEventType {
    FormSubmission,

    BeaconDisconnect,
    BeaconEnter,
    BeaconExit,

    GeofenceDisconnect,
    GeofenceEnter,
    GeofenceExit,

    MobileLogout,
    Moving,
    Stationary,
    PeerEnter,
    PeerExit,
}

export interface RowEventDefinition {
    eventType: RowEventType;
    eventTitle: string;
    iconHtml: string;
    subEvents?: RowEventDefinition[];
}

export const EVENT_MATCHES: { [key in RowEventType]: RowEventMatch } = {
    [RowEventType.FormSubmission]: EventType.FormSubmission,

    [RowEventType.BeaconDisconnect]: EventType.BeaconDisconnect,
    [RowEventType.BeaconEnter]: EventType.BeaconEnter,
    [RowEventType.BeaconExit]: EventType.BeaconExit,
    [RowEventType.PeerEnter]: EventType.PeerEnter,
    [RowEventType.PeerExit]: EventType.PeerExit,

    [RowEventType.GeofenceDisconnect]: EventType.GeofenceDisconnect,
    [RowEventType.GeofenceEnter]: EventType.GeofenceEnter,
    [RowEventType.GeofenceExit]: EventType.GeofenceExit,

    [RowEventType.MobileLogout]: EventType.MobileLogout,
    [RowEventType.Stationary]: EventType.Stationary,
    [RowEventType.Moving]: EventType.Moving,
};

export const GEOFENCE_EVENT_TYPES = [
    RowEventType.GeofenceDisconnect,
    RowEventType.GeofenceEnter,
    RowEventType.GeofenceExit,
];

export const BEACON_EVENT_TYPES = [
    RowEventType.BeaconDisconnect,
    RowEventType.BeaconEnter,
    RowEventType.BeaconExit,
];

export interface SafetyObservationEvent extends AbstractEvent {
    type: EventType.SafetyObservationResolved | EventType.SafetyObservationSubmitted | EventType.SafetyObservationUpdated;
    geofenceId?: string;
    userResponse?: string;
    hazardLevel?: 'low' | 'medium' | 'high'; // leaving in for trigger case
    safetyObservationId?: string;
    attachments?: MessageAttachment[];
}

export interface FacilityEvent extends AbstractEvent {
    type: EventType.FacilityEnter | EventType.FacilityExit;
    waltId?: string;
    waltCrafts?: string[];
    facilityId?: string;
    facilityName?: string;
    facilityTags?: string[];
    facilityEntered?: Date;
    disconnect?: boolean;
}

export interface GasMonitorReadingChangesEvent extends AbstractEvent {
    type: EventType.GasMonitorReading;
    readings: GasSensorReading[];
    geofenceId?: string;
    changes: Array<Partial<GasMonitorEvent>>;
}

export interface BeaconEvent extends AbstractEvent {
    type: EventType.BeaconEnter | EventType.BeaconExit | EventType.BeaconDisconnect;
    beaconId?: string;
    beaconName?: string;
    beaconLevel?: number;
    beaconEntered?: Date;
    disconnect?: boolean;
}

export interface BeaconLowBatteryEvent extends AbstractEvent {
    type: EventType.BeaconLowBattery;
    beaconId?: string;
    beaconName?: string;
    beaconLevel?: number;
    battery?: number;
    facilityId?: string;
}

/**
 * Raised when a walt acting as a mesh repeater is running down its own battery, which means it has
 * lost external power.
 */
export interface WaltRepeaterPowerLossEvent extends AbstractEvent {
    type: EventType.WaltRepeaterPowerLoss;
    waltId?: string;
    battery?: number;
    repeaterNickname?: string;
}

export interface PeerEvent extends AbstractEvent {
    type: EventType.PeerEnter | EventType.PeerExit;
    peerId?: string;
    peerEntered?: Date;
    disconnect?: boolean;
}

export interface LoginEvent extends AbstractEvent {
    type: EventType.MobileLogin;
}

export interface WaltEvent extends AbstractEvent {
    type: WaltEventTypes;
    personId?: string;
    wrangler: WranglerPlacement;
    /** Why the walt powered off, as it reported; set only on a `WaltPoweredOff` event. */
    reason?: string;
}

export interface LevelEvent extends AbstractEvent {
    type: EventType.LevelEnter | EventType.LevelExit;
    level: number;
}

export interface WeatherAlertEvent extends AbstractEvent {
    /**
     * `WeatherAlert` for an issued alert; `WeatherAlertResolved` or `WeatherAlertWarningResolved`
     * for its all-clear (the latter only when the resolved alert was Warning-level) - a distinct
     * type per case rather than a flag, so an existing/future rule scoped to one can never also
     * match another's.
     */
    type: EventType.WeatherAlert | EventType.WeatherAlertResolved | EventType.WeatherAlertWarningResolved;
    /** See `WeatherAlert.eventCode`'s own doc comment - one code per system NWS publishes it under, always an array. */
    eventCode?: string[];
    event?: string;
    headline?: string;
    severity?: CapAlertSeverity;
    urgency?: CapAlertUrgency;
    certainty?: CapAlertCertainty;
    onset?: Date; // ISO 8601, unformatted
    expires?: Date; // ISO 8601, unformatted
    areaDesc?: string;
    description?: string;
    instruction?: string;
    resolvedReason?: 'cancelled' | 'expired' | 'noLongerMatched';
}

type EventIgnores = 'type' | 'id';

// convenient type with all event properties possible for the backend record
export type AnyEvent =
    Omit<Event, EventIgnores> &
    Omit<AirReadingSubmissionEvent, EventIgnores> &
    Omit<FormSubmissionEvent, EventIgnores> &
    Omit<GeofenceEvent, EventIgnores> &
    Omit<FacilityEvent, EventIgnores> &
    Omit<PanicEvent, EventIgnores> &
    Omit<GasMonitorEvent, EventIgnores> &
    Omit<GasMonitorReadingChangesEvent, EventIgnores> &
    Omit<BeaconEvent, EventIgnores> &
    Omit<BeaconLowBatteryEvent, EventIgnores> &
    Omit<PeerEvent, EventIgnores> &
    Omit<LoginEvent, EventIgnores> &
    Omit<LogoutEvent, EventIgnores> &
    Omit<WaltEvent, EventIgnores> &
    Omit<WaltRepeaterPowerLossEvent, EventIgnores> &
    Omit<SafetyObservationEvent, EventIgnores> &
    Omit<WeatherAlertEvent, EventIgnores> & {
    id: EventId;
    type: EventType;
};

export type EventResponse = Omit<AnyEvent, 'id'> & { id: string, date: Date };

export interface BadgeEvent extends Omit<AbstractEvent, 'id'> {
    id?: string;
    personId?: string;
    date?: string;

    location?: BadgeLocation;
    level?: number;
    accuracy?: number;
    battery?: number;
    connectivity?: ConnectivityStatus;
    panic?: boolean;
    message?: string;
    gasMonitor?: GasMonitorReading;

    itemName?: string; // Not part of backend model
    formName?: string; // Not part of backend model
}

// convenient interface with all possible properties for frontend / mobile use, with sanitized properties
export type AnyBadgeEvent = BadgeEvent &
    Omit<Partial<AirReadingSubmissionEvent>, EventIgnores> &
    Omit<Partial<FormSubmissionEvent>, EventIgnores> &
    Omit<Partial<BeaconEvent>, EventIgnores> &
    Omit<Partial<GeofenceEvent>, EventIgnores> &
    Omit<Partial<PanicEvent>, EventIgnores> &
    Omit<Partial<FacilityEvent>, EventIgnores> &
    Omit<Partial<LogoutEvent>, EventIgnores>;
